import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ClipboardList, LoaderCircle, Package, Settings2, ShoppingCart, Wallet } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { borderPrimary, cardBg, primaryText, secondaryText, tertiaryText, quaternaryText, redTextColor, semiBoldText, smallTextSize, regularText, textBlue, lightBlueBG, roundedLG, iconBgGreen, iconBgPurple, iconBgBlue, iconBgRed } from '../../../styling/stylingHelper';
import { currencyFormatter } from '../../../services/helperService';
import { ExpectedInventoryBootstrap, ExpectedInventorySettingsUpdate, fetchExpectedInventory, updateExpectedInventorySettings } from '../../../services/inventory/expectedInventoryService';
import { useLoadOrderHistory, useLoadRevenueHistory, useLoadCostPrice, useLoadSubrecipes } from '../../../utils/dataLoadingUtils';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useSubrecipesStore } from '../../../stores/appSubrecipesStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { computeExpectedInventory } from '../../../utils/reports/expectedInventory';
import { StatCard } from '../../ui/StatCard';
import { TableComponent, TableComponentProps } from '../../ui/TableComponent';
import { TabbedSections } from '../../ui/TabbedSections';
import { fmtQuantity, renderSupplier } from './inventoryReviewShared';
import { InventoryProductSearchInput } from './inventoryProductMobileCard';
import { supplierText } from './inventoryCountExport';
import type { ExpectedProductRow } from '../../../utils/reports/expectedInventory';
import OrderListModal from './OrderListModal';
import ExpectedInventorySettingsPanel from './ExpectedInventorySettingsPanel';

interface Props { restaurantId: string; initialGroupId: string | null }
const buttonClass = cn('min-h-[44px] px-3 py-2 border rounded-xl text-sm font-medium disabled:opacity-50', borderPrimary, cardBg, primaryText);
const primaryButtonClass = cn(smallTextSize, semiBoldText, 'inline-flex items-center gap-1.5 h-11 px-4 rounded-xl text-white dark:text-white bg-[#0E8965] dark:bg-[#0E8965] hover:bg-[#0E8965]/95 transition-colors shrink-0 disabled:opacity-50');
const MOBILE_BATCH = 20;
type SortKey = 'productName' | 'inventoryCategoryName' | 'expected' | 'toOrder' | 'value';

export default function ExpectedInventoryTab(props: Props) {
  const [attempt, setAttempt] = useState(0);
  return <ExpectedInventoryView key={`${props.restaurantId}:${attempt}`} {...props} onRetry={() => setAttempt(value => value + 1)} />;
}

function ExpectedInventoryView({ restaurantId, initialGroupId, onRetry }: Props & { onRetry: () => void }) {
  const { t, i18n } = useTranslation();
  useLoadOrderHistory();
  useLoadRevenueHistory();
  useLoadCostPrice();
  useLoadSubrecipes();
  const orders = useOrderHistory();
  const revenue = useRevenueHistory();
  const cost = useCostPriceStore();
  const recipes = useSubrecipesStore();
  const restaurantDataLoaded = useRestaurantStore(state => state.isDataLoaded);
  const [bootstrap, setBootstrap] = useState<ExpectedInventoryBootstrap | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [groupId, setGroupId] = useState(initialGroupId);
  const [search, setSearch] = useState('');
  const [belowOnly, setBelowOnly] = useState(false);
  const [visibleCount, setVisibleCount] = useState(MOBILE_BATCH);
  const [categoryFilters, setCategoryFilters] = useState<string[]>([]);
  const [sortColumn, setSortColumn] = useState<SortKey | null>('inventoryCategoryName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>('asc');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [orderListOpen, setOrderListOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const mounted = useRef(false);
  const saveInFlight = useRef(false);

  useEffect(() => {
    let cancelled = false;
    mounted.current = true;
    fetchExpectedInventory(restaurantId).then(data => {
      if (cancelled) return;
      setBootstrap(data);
    }).catch(error => {
      console.error('Failed to load expected inventory:', error);
      if (!cancelled) setLoadFailed(true);
    });
    return () => { cancelled = true; mounted.current = false; };
  }, [restaurantId]);

  const ready = bootstrap?.restaurantId === restaurantId &&
    orders.currentRestaurantId === restaurantId && orders.fullyLoaded && !orders.isLoading &&
    revenue.currentRestaurantId === restaurantId && revenue.fullyLoaded && !revenue.isLoading &&
    cost.currentRestaurantId === restaurantId && !cost.isLoading && !cost.error &&
    recipes.currentRestaurantId === restaurantId && !recipes.isLoading && !recipes.error;
  const loadingSources = orders.isLoading || orders.isBackgroundLoading || revenue.isLoading || revenue.isBackgroundLoading || cost.isLoading || recipes.isLoading || !restaurantDataLoaded;
  // A failed progressive request can finish without setting fullyLoaded. Never
  // project its partial history as if the missing activity were zero.
  const failed = loadFailed || (!!bootstrap && !ready && !loadingSources);
  const projection = useMemo(() => ready && bootstrap ? computeExpectedInventory({
    countsHistory: bootstrap.counts,
    groups: bootstrap.groups,
    sections: bootstrap.sections,
    sectionProducts: bootstrap.products,
    minimums: bootstrap.minimums,
    settings: bootstrap.settings,
    orders: orders.orderHistoryList,
    revenue: revenue.revenueHistoryList,
    costPriceList: cost.costPriceList,
    subrecipesById: recipes.subrecipesById,
    defaultWastePercentage: bootstrap.defaultWastePercentage,
    currentRestaurantId: restaurantId,
    now: Date.now(),
  }) : null, [ready, bootstrap, orders.orderHistoryList, revenue.revenueHistoryList, cost.costPriceList, recipes.subrecipesById, restaurantId]);

  const selectedGroupId = bootstrap?.groups.some(group => group.id === groupId) ? groupId : bootstrap?.groups[0]?.id;
  const group = projection?.groups.find(candidate => candidate.groupKey === selectedGroupId);
  const categoryOf = (product: ExpectedProductRow) => product.inventoryCategoryName ?? t('inventory.uncategorizedBucket');
  const categoryOptions = Array.from(new Set((group?.products ?? []).map(categoryOf))).sort((a, b) => a.localeCompare(b));
  const query = search.trim().toLocaleLowerCase();
  const filtered = (group?.products ?? []).filter(product => (!belowOnly || product.belowMinimum)
    && (categoryFilters.length === 0 || categoryFilters.includes(categoryOf(product)))
    && (!query || product.productName.toLocaleLowerCase().includes(query) || supplierText(product.supplier).toLocaleLowerCase().includes(query)));
  const sortValue = (product: ExpectedProductRow, key: SortKey): string | number => {
    switch (key) {
      case 'productName': return product.productName.toLocaleLowerCase();
      // Category order follows the restaurant's own category positions; uncategorized last.
      case 'inventoryCategoryName': return product.inventoryCategoryPosition ?? Number.MAX_SAFE_INTEGER;
      case 'expected': return product.estimatedCountQty ?? -Infinity;
      case 'toOrder': return product.toOrder?.qty ?? -Infinity;
      case 'value': return product.estimatedValue ?? -Infinity;
    }
  };
  const sorted = !sortColumn || !sortDirection ? filtered : [...filtered].sort((a, b) => {
    const av = sortValue(a, sortColumn), bv = sortValue(b, sortColumn);
    const dir = sortDirection === 'asc' ? 1 : -1;
    return av < bv ? -dir : av > bv ? dir : a.productName.localeCompare(b.productName);
  });
  const visible = sorted.slice(0, visibleCount);
  const groupName = (id: string) => bootstrap?.groups.find(candidate => candidate.id === id)?.name ?? t('inventory.ungrouped');
  const dateLabel = (date: string) => new Date(`${date}T12:00:00Z`).toLocaleDateString(i18n.language, { timeZone: 'Europe/Amsterdam', day: 'numeric', month: 'short', year: 'numeric' });
  const baselineLabel = (dates: string[]) => t('inventory.expected.basedOn', { dates: dates.map(dateLabel).join(', ') });

  useEffect(() => { setVisibleCount(MOBILE_BATCH); setOrderListOpen(false); }, [selectedGroupId, belowOnly, search, categoryFilters]);
  useEffect(() => { setCategoryFilters([]); }, [selectedGroupId]);

  const handleSort = (column: string) => {
    const key = column as SortKey;
    if (sortColumn === key) setSortDirection(direction => direction === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(key); setSortDirection(key === 'productName' || key === 'inventoryCategoryName' ? 'asc' : 'desc'); }
  };

  const saveSettings = async (update: ExpectedInventorySettingsUpdate) => {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    setSaving(true);
    setSaveFailed(false);
    try {
      const settings = await updateExpectedInventorySettings(restaurantId, update);
      if (mounted.current) setBootstrap(current => current ? { ...current, settings } : current);
    } catch (error) {
      console.error('Failed to save expected inventory settings:', error);
      if (mounted.current) setSaveFailed(true);
    } finally {
      saveInFlight.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  if (!projection || !bootstrap) return <div className="flex flex-col items-center gap-4 py-16">
    {failed ? <p role="alert" className={secondaryText}>{t('inventory.expected.loadFailed')}</p> : <LoaderCircle className={cn('h-8 w-8 animate-spin', primaryText)} aria-label={t('inventory.loading')} />}
    {failed && <button type="button" onClick={onRetry} className={buttonClass}>{t('inventory.expected.retry')}</button>}
  </div>;

  const hints = (product: ExpectedProductRow) => <>
    {product.notCounted && <span className={cn('block text-xs mt-0.5', secondaryText)}>{t('inventory.expected.notCounted')}</span>}
    {product.otherGroupIds.length > 0 && <span className={cn('block text-xs mt-0.5', tertiaryText)}>{t('inventory.expected.alsoIn', { groups: product.otherGroupIds.map(groupName).join(', ') })}</span>}
    {product.estimatedAllocation && <span className={cn('block text-xs mt-0.5', tertiaryText)}>{t('inventory.expected.allocationEstimate')}</span>}
  </>;
  const productName = (product: ExpectedProductRow) => <div className="min-w-0">
    <span className={cn(primaryText, smallTextSize, semiBoldText, 'break-words')}>{product.productName}</span>
    {product.negative && <AlertTriangle className={cn('inline ml-1.5 h-4 w-4 align-text-bottom', redTextColor)} aria-label={t('inventory.estFlag.negative')} />}
    {hints(product)}
  </div>;
  // Same blue quantity pill as Count Review; red when the projection is negative.
  const expectedPill = (product: ExpectedProductRow) => product.estimatedCountQty === null
    ? <span className={secondaryText}>—</span>
    : <span className={cn(smallTextSize, semiBoldText, roundedLG, 'px-2 py-1 whitespace-nowrap',
        product.negative ? 'bg-[#FEE4E2] text-[#D92D20] dark:bg-[#D92D2026] dark:text-[#F97066]' : cn(textBlue, lightBlueBG, 'dark:bg-[#2E90FA26] dark:text-[#2E90FA]'))}>
      {`${fmtQuantity(product.estimatedCountQty)} ${product.countUnit}`}
    </span>;
  const minimumText = (product: ExpectedProductRow) => product.minimum ? `${fmtQuantity(product.minimum.qty)} ${product.minimum.unit}` : '—';
  const toOrderText = (product: ExpectedProductRow) => product.toOrder ? `${fmtQuantity(product.toOrder.qty)} ${product.toOrder.unit}` : '—';
  const money = (amount: number | null) => amount === null ? '—' : currencyFormatter.format(amount);
  const productNotes = Object.fromEntries((group?.products ?? []).filter(product => product.otherGroupIds.length > 0).map(product => [product.key, t('inventory.expected.alsoIn', { groups: product.otherGroupIds.map(groupName).join(', ') })]));

  const columns: TableComponentProps<ExpectedProductRow>['columns'] = [
    { key: 'productName', label: t('inventory.product'), sortable: true, sortKey: 'productName', render: (_v, row) => productName(row) },
    { key: 'supplier', label: t('inventory.colSupplier'), columnType: 'category', render: (_v, row) => renderSupplier(row.supplier) },
    { key: 'inventoryCategoryName', label: t('inventory.colInvCategory'), columnType: 'category',
      filter: { selectedValues: categoryFilters, onValuesChange: setCategoryFilters, availableOptions: categoryOptions, valueType: 'default', filterLabel: t('inventory.colInvCategory') },
      render: (_v, row) => <span className={cn(secondaryText, smallTextSize)}>{categoryOf(row)}</span> },
    { key: 'estimatedCountQty', label: t('inventory.expected.colExpected'), sortable: true, sortKey: 'expected', render: (_v, row) => expectedPill(row) },
    { key: 'minimum', label: t('inventory.expected.colMinimum'), render: (_v, row) => <span className={cn(secondaryText, smallTextSize, 'whitespace-nowrap')}>{minimumText(row)}</span> },
    { key: 'toOrder', label: t('inventory.expected.colToOrder'), sortable: true, sortKey: 'toOrder',
      render: (_v, row) => <span className={cn(smallTextSize, 'whitespace-nowrap', row.toOrder ? cn(semiBoldText, redTextColor) : secondaryText)}>{toOrderText(row)}</span> },
    { key: 'estimatedValue', label: t('inventory.value'), sortable: true, sortKey: 'value',
      render: (_v, row) => <span className={cn(smallTextSize, semiBoldText, row.estimatedValue !== null && row.estimatedValue < 0 ? redTextColor : primaryText)}>{money(row.estimatedValue)}</span> },
  ];
  const belowMinCount = group?.belowMinProducts.length ?? 0;
  const filterTabs = <TabbedSections options={[{ value: 'all', label: t('inventory.expected.filterAll') }, { value: 'below', label: t('inventory.expected.belowMinimum') }]}
    value={belowOnly ? 'below' : 'all'} onChange={value => setBelowOnly(value === 'below')} isTimeOptionSelection />;

  return <div>
    <div className="mb-5 sm:mb-6 flex flex-wrap items-center gap-3">
      <select aria-label={t('inventory.groupName')} value={selectedGroupId ?? ''} onChange={event => setGroupId(event.target.value)} className={cn(buttonClass, 'max-w-full')}>
        {bootstrap.groups.map(candidate => <option key={candidate.id} value={candidate.id}>{candidate.name}</option>)}
      </select>
      <button type="button" onClick={() => setSettingsOpen(true)} className={cn(buttonClass, 'flex items-center gap-2')}><Settings2 className="h-4 w-4" />{t('inventory.expected.settingsTitle')}</button>
      <button type="button" disabled={belowMinCount === 0} onClick={() => setOrderListOpen(true)} className={cn(primaryButtonClass, 'sm:ml-auto')}><ShoppingCart className="h-4 w-4" />{t('inventory.expected.seeOrderList')}</button>
    </div>
    {!group ? <p className={cn('py-10 text-center', secondaryText)}>{t('inventory.estimatedEmpty')}</p> : <>
      <div className="mb-5 sm:mb-6 grid gap-2.5 md:gap-6 grid-cols-2 xl:grid-cols-4">
        <div className="h-full min-w-0">
          <StatCard icon={<Wallet className="sm:w-8 sm:h-8 w-[26px] h-[26px] text-[#079455] dark:text-[#47CD89]" />} iconBg={iconBgGreen} title={t('inventory.expected.value')} mobileLayout="tile"
            value={group.estimatedTotalValue} valueType="currency" subtitle={baselineLabel(group.baselineDates)}
            valueClassName={group.estimatedTotalValue < 0 ? redTextColor : undefined} />
        </div>
        <div className="h-full min-w-0">
          <StatCard icon={<ClipboardList className="sm:w-8 sm:h-8 w-[26px] h-[26px] text-[#2E90FA] dark:text-[#53B1FD]" />} iconBg={iconBgBlue} title={t('inventory.expected.kpiCountedValue')} mobileLayout="tile"
            value={group.countedTotalValue} valueType="currency" />
        </div>
        <div className="h-full min-w-0">
          <StatCard icon={<Package className="sm:w-8 sm:h-8 w-[26px] h-[26px] text-[#6938EF] dark:text-[#9B8AFB]" />} iconBg={iconBgPurple} title={t('inventory.expected.kpiProducts')} mobileLayout="tile"
            value={group.products.length} valueType="number" />
        </div>
        <div className="h-full min-w-0">
          <StatCard icon={<ShoppingCart className="sm:w-8 sm:h-8 w-[26px] h-[26px] text-[#D92D20] dark:text-[#F97066]" />} iconBg={iconBgRed} title={t('inventory.kpiNeedsOrdering')} mobileLayout="tile"
            value={belowMinCount} valueType="number" onClick={belowMinCount > 0 ? () => setOrderListOpen(true) : undefined} showArrow={belowMinCount > 0} />
        </div>
      </div>

      <div className="hidden md:block">
        <TableComponent
          title={t('inventory.expected.productsTitle')}
          data={sorted}
          columns={columns}
          usePagination
          blankLines={false}
          headerActions={<div className="flex items-center gap-3 flex-wrap justify-end">
            {filterTabs}
            <InventoryProductSearchInput value={search} onChange={setSearch} placeholder={t('inventory.searchProducts')} />
          </div>}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          activeFilterCount={(belowOnly ? 1 : 0) + (query ? 1 : 0)}
          onClearFilters={() => { setBelowOnly(false); setSearch(''); setCategoryFilters([]); }}
          options={{ borderless: true, leftAlignCells: true, noBlankPadding: true }}
        />
      </div>

      <div className="md:hidden flex flex-col gap-4">
        <h2 className={cn(semiBoldText, primaryText, 'text-[18px] leading-[24px]')}>{t('inventory.expected.productsTitle')}</h2>
        {filterTabs}
        <InventoryProductSearchInput value={search} onChange={setSearch} placeholder={t('inventory.searchProducts')} />
        {visible.length === 0 ? <p className={cn(smallTextSize, regularText, quaternaryText, 'text-center py-8')}>{t('inventory.expected.noProducts')}</p> : <div className="flex flex-col gap-3">
          {visible.map(product => <div key={product.key} className="rounded-xl bg-white dark:bg-[#0E2B42] p-4">
            <div className="flex items-start justify-between gap-3 mb-3">{productName(product)}<span className="shrink-0">{expectedPill(product)}</span></div>
            <div className="grid grid-cols-3 border-y border-[#EAECF0] dark:border-borderPrimary-dark py-3 divide-x divide-[#EAECF0] dark:divide-borderPrimary-dark">
              <MobileInfo label={t('inventory.colInvCategory')} value={categoryOf(product)} />
              <MobileInfo label={t('inventory.expected.colMinimum')} value={minimumText(product)} />
              <MobileInfo label={t('inventory.expected.colToOrder')} value={<span className={product.toOrder ? redTextColor : undefined}>{toOrderText(product)}</span>} />
            </div>
            <div className="flex items-center justify-between gap-3 pt-3">
              <span className={cn(tertiaryText, 'text-[14px] truncate')}>{supplierText(product.supplier)}</span>
              <span className={cn('text-[14px] font-medium', product.estimatedValue !== null && product.estimatedValue < 0 ? redTextColor : primaryText)}>{money(product.estimatedValue)}</span>
            </div>
          </div>)}
          {visible.length < sorted.length && <button type="button" onClick={() => setVisibleCount(count => count + MOBILE_BATCH)} className={cn(buttonClass, 'w-full')}>{t('common.loadMore')}</button>}
        </div>}
      </div>
    </>}
    {settingsOpen && <ExpectedInventorySettingsPanel projection={projection} baselineKeys={group?.baselineKeys ?? []} groupName={groupName} saving={saving} saveError={saveFailed} onSave={update => void saveSettings(update)} onClose={() => setSettingsOpen(false)} />}
    {orderListOpen && group && <OrderListModal isOpen onClose={() => setOrderListOpen(false)} products={group.belowMinProducts} subtitle={t('inventory.expected.orderSubtitle')} productNotes={productNotes} />}
  </div>;
}

function MobileInfo({ label, value }: { label: string; value: ReactNode }) {
  return <div className="flex flex-col gap-0.5 px-3 first:pl-0 last:pr-0 min-w-0">
    <span className={cn(tertiaryText, 'text-[12px] leading-[18px] font-medium truncate')}>{label}</span>
    <span className={cn(primaryText, smallTextSize, semiBoldText, 'truncate leading-5')}>{value}</span>
  </div>;
}
